import { deburr } from 'lodash';
import { BankType, BrandType, DebtType, NegotiationOption, NegotiationOptionAlternative, NegotiationOptionAlternativeType, NegotiationState, NegotiationType, OutDebt, OutDebtState } from '../generated/graphql';

const {
  Traditional, LowRate, NoPayment,
  NoPrepaymentPreferentialRate, NoPrepaymentWeightedRate,
  PrepaymentPreferentialRate, PrepaymentWeightedRate,
  DiscountInterest, DiscountInterestLowRate,
} = NegotiationOptionAlternativeType;
const { SpecialCampaign, SpecialCampaignLowRate } = NegotiationOptionAlternativeType;

export const getNegBooleans = (
  outDebt?: Pick<OutDebt, 'negotiation' | 'type' | 'subType' | 'extra' | 'bank' | 'brand'>,
  option?: Pick<NegotiationOption, 'type' | 'alternatives'>,
  alternative?: Pick<NegotiationOptionAlternative, 'type'>,
) => {
  const opType = outDebt?.negotiation?.type || option?.type;
  const optAlternativeHasType = !!outDebt?.negotiation?.alternative.type
    || !!option?.alternatives.some((alt) => !!alt.type);
  const isNanocredit = outDebt?.subType === 'NANOCREDITO';
  const negState = outDebt?.negotiation?.state || NegotiationState.ConfirmedByUser;
  const hasNeg = !!outDebt?.negotiation;
  const isConfirmedByUser = negState === NegotiationState.ConfirmedByUser;
  const isSentToBank = negState === NegotiationState.SentToBank;
  const isApprovedByBank = negState === NegotiationState.ApprovedByBank;
  const isRejected = negState === NegotiationState.RejectedByBank;
  const isExpired = negState === NegotiationState.Expired;
  const isPrepaymentCompleted = negState === NegotiationState.PrepaymentCompleted;
  const isPrepaymentIncompleted = negState === NegotiationState.PrepaymentIncomplete;
  const isPrepaymentCompletedOrAfter = isSentToBank || isApprovedByBank || isPrepaymentCompleted;

  // Brands
  const isTranqui = outDebt?.brand === BrandType.Tranquiapp;
  const isBeta = outDebt?.brand === BrandType.Beta;
  const isJcap = outDebt?.brand === BrandType.Jcap;
  const isApex = outDebt?.brand === BrandType.Apex;
  const isPra = outDebt?.brand === BrandType.Pra;
  // Banks
  const isDav = isTranqui && outDebt?.bank === BankType.BancoDavivienda;
  const isBbva = isTranqui && outDebt?.bank === BankType.Bbva;
  const isRappiPay = isTranqui && outDebt?.bank === BankType.Rappipay;
  const isRappiPrestamo = isTranqui && outDebt?.bank === BankType.RappiPrestamo;
  const isPichincha = isTranqui && outDebt?.bank === BankType.Pichincha;
  const isMovistarMoney = isTranqui && outDebt?.bank === BankType.MovistarMoney;
  const isBogota = isTranqui && outDebt?.bank === BankType.BancoDeBogota;
  const isScotiabank = isTranqui && outDebt?.bank === BankType.Scotiabank;
  const isNoBank = isTranqui && outDebt?.bank === BankType.NoBank;
  const isClaro = isTranqui && outDebt?.bank === BankType.Claro;
  const isLulo = isTranqui && outDebt?.bank === BankType.LuloBank;
  const isApexRappi = isApex && (outDebt?.bank === BankType.Rappipay
    || outDebt?.extra?.mainBank === BankType.Rappipay);
  const isApexPopular = isApex && (outDebt?.bank === BankType.BancoPopular
    || outDebt?.extra?.mainBank === BankType.BancoPopular);
  const isApexOccidente = isApex && (outDebt?.bank === BankType.Occidente
    || outDebt?.extra?.mainBank === BankType.Occidente);

  const isTotalPayment = opType === NegotiationType.TotalPayment;
  const isPaymentPromise = opType === NegotiationType.PaymentPromise;
  const isPartialPaymentPromise = isPaymentPromise && optAlternativeHasType;
  const isRestructure = opType === NegotiationType.Restructure;
  const isRefinance = opType === NegotiationType.Refinance;
  const isModification = opType === NegotiationType.Modification;
  const isExpressRefinance = opType === NegotiationType.ExpressRefinance;
  const isNormalization = opType === NegotiationType.Normalization;
  const isACPK = opType === NegotiationType.PenaltyPortfolioPaymentAgreement;
  const isInstallmentsPlan = opType === NegotiationType.InstallmentsPlan;
  const isCondonation = opType === NegotiationType.Condonation;
  const isCatchUp = opType === NegotiationType.CatchUp;
  const isQuotification = opType === NegotiationType.Quotification;
  const isNoOptions = opType === NegotiationType.NoOptions;
  const isTermExtension = opType === NegotiationType.TermExtension;
  const isVirtualDebt = outDebt?.type === DebtType.Virtual;
  const isPunished = outDebt?.extra?.state === OutDebtState.Punished;
  const isBalanceBehindPayment = opType === NegotiationType.BalanceBehindPayment;
  const isCreditCard = outDebt?.type === DebtType.CreditCard;
  const isCreditoSi = outDebt?.subType === 'credito si';

  // Banco de Bogotá
  const optAlternative = outDebt?.negotiation?.alternative
    || option?.alternatives.find((alt) => alt.type);
  const optAlternativeType = optAlternative?.type;
  const isBogotaTraditional = isBogota && optAlternativeType === Traditional;
  const isBogotaLowRate = isBogota && optAlternativeType === LowRate;
  const isBogotaNoPayment = isBogota && optAlternativeType === NoPayment;
  // campaign alternatives share one option, so use the selected one before negotiation creation
  const campaignAlternative = outDebt?.negotiation?.alternative || alternative || optAlternative;
  // identified by type: sin pago campaigns (NO_PAYMENT/LOW_RATE) keep their original UI
  const campaignType = campaignAlternative?.type;
  const isBogotaSpecialCampaign = isBogota && isBalanceBehindPayment
    && (campaignType === SpecialCampaign || campaignType === SpecialCampaignLowRate);
  const isBogotaSpecialCampaignLowRate = isBogotaSpecialCampaign
    && campaignType === SpecialCampaignLowRate;
  const isBogotaNoPrepaymentPreferentialRate = isBogota
    && optAlternativeType === NoPrepaymentPreferentialRate;
  const isBogotaNoPrepaymentWeightedRate = isBogota
    && optAlternativeType === NoPrepaymentWeightedRate;
  const isBogotaPrepaymentPreferentialRate = isBogota
    && optAlternativeType === PrepaymentPreferentialRate;
  const isBogotaPrepaymentWeightedRate = isBogota && optAlternativeType === PrepaymentWeightedRate;
  const isBogotaTermExtension = isBogota && isTermExtension;
  const isBogotaTermExtensionDiscountInterest = isBogotaTermExtension
    && optAlternativeType === DiscountInterest;
  const isBogotaTermExtensionDiscountInterestLowRate = isBogotaTermExtension
    && optAlternativeType === DiscountInterestLowRate;
  const isBalanceBehindLowRate = isBalanceBehindPayment && isBogotaLowRate;

  const isMovistarMoneyInvoice = deburr(outDebt?.extra?.movistarInvoice ?? '').toLowerCase() === 'si';
  const isCeded = outDebt?.extra?.isCededed || false;

  return {
    hasNeg,
    opType,
    negState,
    isNanocredit,
    isPrepaymentCompleted,
    isRestructure,
    isConfirmedByUser,
    isApprovedByBank,
    isSentToBank,
    isRejected,
    isExpired,
    isTotalPayment,
    isPaymentPromise,
    isPartialPaymentPromise,
    isACPK,
    isInstallmentsPlan,
    isCondonation,
    isQuotification,
    isCatchUp,
    isVirtualDebt,
    isRefinance,
    isModification,
    isNormalization,
    isNoOptions,
    isPunished,
    isDav,
    isBbva,
    isRappiPay,
    isRappiPrestamo,
    isPichincha,
    isBeta,
    isJcap,
    isApex,
    isPra,
    isTranqui,
    isMovistarMoney,
    isBogota,
    isScotiabank,
    isNoBank,
    isClaro,
    isLulo,
    isExpressRefinance,
    isPrepaymentIncompleted,
    isBalanceBehindPayment,
    isBogotaTraditional,
    isBogotaLowRate,
    isBogotaSpecialCampaign,
    isBogotaSpecialCampaignLowRate,
    isBalanceBehindLowRate,
    isBogotaNoPayment,
    isCreditCard,
    isCreditoSi,
    isBogotaNoPrepaymentPreferentialRate,
    isBogotaNoPrepaymentWeightedRate,
    isBogotaPrepaymentPreferentialRate,
    isBogotaPrepaymentWeightedRate,
    isBogotaTermExtension,
    isBogotaTermExtensionDiscountInterest,
    isBogotaTermExtensionDiscountInterestLowRate,
    isTermExtension,
    isPrepaymentCompletedOrAfter,
    isMovistarMoneyInvoice,
    isCeded,
    isApexRappi,
    isApexPopular,
    isApexOccidente,
  };
};
